/* lex.ivoliq — Stammdaten-Verwaltung (Juli 2026):
   Kanzlei (Empfängerin der Honorarnoten, Kanzleiwechsel möglich) + Vollmachten.
   Person/Anschrift/Steuer-IDs/Bank bleiben im Profil-Tab (Autosave via /settings). */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Landmark, ShieldCheck, GraduationCap } from "lucide-react";
import { SALUTATIONS, TITLE_PREFIXES, TITLE_SUFFIXES } from "@/components/lex/lexTitles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LexStammdatenTab = () => {
  const [kanzlei, setKanzlei] = useState({ name: "", email: "", street: "", postal_code: "", city: "", vat_id: "" });
  const [vollmachten, setVollmachten] = useState({ stb_vollmacht: true, finanzonline: true, bank_consent: false });
  const [profil, setProfil] = useState({ salutation: "", title_prefix: "", title_suffix: "", name: "" });
  const [loading, setLoading] = useState(true);
  const [savingKanzlei, setSavingKanzlei] = useState(false);
  const [savingProfil, setSavingProfil] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lex/stammdaten`)
      .then((res) => {
        if (res.data.kanzlei) setKanzlei((k) => ({ ...k, ...Object.fromEntries(Object.entries(res.data.kanzlei).map(([a, b]) => [a, b || ""])) }));
        if (res.data.vollmachten) setVollmachten((v) => ({ ...v, ...res.data.vollmachten }));
        if (res.data.profil) setProfil((p) => ({ ...p, ...res.data.profil }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveProfil = async () => {
    setSavingProfil(true);
    try {
      const res = await axios.put(`${API}/lex/stammdaten/profil`, {
        salutation: profil.salutation, title_prefix: profil.title_prefix, title_suffix: profil.title_suffix,
      });
      toast.success(`Gespeichert — Anrede: „${res.data.anrede}", Honorarnote: „${res.data.briefkopf}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setSavingProfil(false);
    }
  };

  const saveKanzlei = async () => {
    if (kanzlei.name.trim().length < 2) {
      toast.error("Bitte den Namen der Kanzlei angeben.");
      return;
    }
    setSavingKanzlei(true);
    try {
      await axios.put(`${API}/lex/stammdaten/kanzlei`, kanzlei);
      toast.success("Kanzlei-Daten gespeichert — gilt für alle zukünftigen Honorarnoten.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setSavingKanzlei(false);
    }
  };

  const toggleVollmacht = async (key, value) => {
    const next = { ...vollmachten, [key]: value };
    setVollmachten(next);
    try {
      await axios.put(`${API}/lex/stammdaten/vollmachten`, next);
      toast.success("Vollmachten aktualisiert.");
    } catch {
      setVollmachten(vollmachten);
      toast.error("Änderung fehlgeschlagen.");
    }
  };

  if (loading) return <div className="text-sm text-slate-400 py-4">Lade Stammdaten …</div>;

  const set = (field) => (e) => setKanzlei((k) => ({ ...k, [field]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="lex-stammdaten-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4" style={{ color: "#C5A059" }} />
            Titel &amp; Anrede
          </CardTitle>
          <CardDescription>
            Der vorangestellte Titel erscheint in der Anrede (z.&nbsp;B. „Herr Mag. Mustermann"),
            der nachgestellte nur auf der Honorarnote.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Anrede</Label>
              <select className="w-full border border-slate-200 bg-white px-3 py-2 text-sm h-10"
                value={profil.salutation} onChange={(e) => setProfil((p) => ({ ...p, salutation: e.target.value }))}
                data-testid="lex-sd-salutation-select">
                <option value="">— keine —</option>
                {SALUTATIONS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Titel (vorangestellt)</Label>
              <select className="w-full border border-slate-200 bg-white px-3 py-2 text-sm h-10"
                value={profil.title_prefix} onChange={(e) => setProfil((p) => ({ ...p, title_prefix: e.target.value }))}
                data-testid="lex-sd-title-prefix-select">
                <option value="">— kein Titel —</option>
                {TITLE_PREFIXES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Titel (nachgestellt)</Label>
              <select className="w-full border border-slate-200 bg-white px-3 py-2 text-sm h-10"
                value={profil.title_suffix} onChange={(e) => setProfil((p) => ({ ...p, title_suffix: e.target.value }))}
                data-testid="lex-sd-title-suffix-select">
                <option value="">— kein Titel —</option>
                {TITLE_SUFFIXES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-testid="lex-sd-titel-preview">
            Vorschau — Anrede: <span className="font-medium text-slate-700">
              {[profil.salutation, profil.title_prefix, profil.name].filter(Boolean).join(" ") || "—"}</span>
            {" · "}Honorarnote: <span className="font-medium text-slate-700">
              {([profil.title_prefix, profil.name].filter(Boolean).join(" ") + (profil.title_suffix ? `, ${profil.title_suffix}` : "")) || "—"}</span>
          </p>
          <Button onClick={saveProfil} disabled={savingProfil}
            className="rounded-none" style={{ background: "#0A1128", color: "#fff" }} data-testid="lex-sd-titel-save">
            {savingProfil ? "Speichert …" : "Titel speichern"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-4 w-4" style={{ color: "#C5A059" }} />
            Kanzlei — Empfängerin der Honorarnoten
          </CardTitle>
          <CardDescription>
            Bei einem Kanzleiwechsel hier anpassen. Änderungen gelten für alle zukünftigen Honorarnoten — bereits erstellte Noten bleiben unverändert.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Kanzlei-Name *</Label>
              <Input value={kanzlei.name} onChange={set("name")} placeholder="Muster Rechtsanwälte GmbH" data-testid="lex-sd-kanzlei-name" />
            </div>
            <div>
              <Label>E-Mail (Rechnungsempfang)</Label>
              <Input type="email" value={kanzlei.email} onChange={set("email")} placeholder="buchhaltung@kanzlei.at" data-testid="lex-sd-kanzlei-email" />
            </div>
            <div>
              <Label>Straße</Label>
              <Input value={kanzlei.street} onChange={set("street")} placeholder="Rotenturmstraße 12" data-testid="lex-sd-kanzlei-street" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>PLZ</Label>
                <Input value={kanzlei.postal_code} onChange={set("postal_code")} placeholder="1010" data-testid="lex-sd-kanzlei-plz" />
              </div>
              <div className="col-span-2">
                <Label>Ort</Label>
                <Input value={kanzlei.city} onChange={set("city")} placeholder="Wien" data-testid="lex-sd-kanzlei-city" />
              </div>
            </div>
            <div>
              <Label>UID der Kanzlei</Label>
              <Input value={kanzlei.vat_id} onChange={(e) => setKanzlei((k) => ({ ...k, vat_id: e.target.value.toUpperCase() }))}
                placeholder="ATU12345678" data-testid="lex-sd-kanzlei-uid" />
            </div>
          </div>
          <Button onClick={saveKanzlei} disabled={savingKanzlei || kanzlei.name.trim().length < 2}
            className="rounded-none" style={{ background: "#0A1128", color: "#fff" }} data-testid="lex-sd-kanzlei-save">
            {savingKanzlei ? "Speichert …" : "Kanzlei-Daten speichern"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" style={{ color: "#C5A059" }} />
            Vollmachten
          </CardTitle>
          <CardDescription>Grundlage dafür, dass Ihre Steuerberatung ohne Rückfragen für Sie arbeiten kann.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            ["stb_vollmacht", "Steuerliche Vertretung", "Ihre Steuerberatung vertritt Sie gegenüber dem Finanzamt."],
            ["finanzonline", "FinanzOnline-Vollmacht", "Einreichung von UVA & Erklärungen direkt über FinanzOnline."],
            ["bank_consent", "Bank-Einwilligung", "Vorbereitung für die automatische Zahlungszuordnung (PSD2, folgt)."],
          ].map(([key, label, hint]) => (
            <div key={key} className="flex items-start justify-between gap-4 border border-slate-200 p-3" data-testid={`lex-sd-vollmacht-${key}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                {key === "stb_vollmacht" && !vollmachten.stb_vollmacht && (
                  <p className="text-xs font-semibold mt-1" style={{ color: "#B45309" }}>
                    Ohne steuerliche Vertretung kann Ihre Kanzlei UVA & Jahresabschluss nicht übernehmen.
                  </p>
                )}
              </div>
              <Switch checked={Boolean(vollmachten[key])} onCheckedChange={(v) => toggleVollmacht(key, v)}
                data-testid={`lex-sd-vollmacht-switch-${key}`} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default LexStammdatenTab;
